import { sb } from './supabase';
import { fetchBakeOffGateStatus } from './solutionOptions';

/** Maps seeded Define-gate criterion names to readiness checks. */
export const BAKEOFF_CRITERION_CHECKS = {
  'Requirements baseline locked': (s) => s.baselineLocked,
  'Solution options assessed': (s) => s.optionsAssessed,
  'Scenario decision recorded': (s) => s.scenarioDecision,
  'Canonical architecture & ROM': (s) => s.canonicalArchitecture && s.hasBusinessCase,
};

export function isBakeOffCriterion(name) {
  return Object.prototype.hasOwnProperty.call(BAKEOFF_CRITERION_CHECKS, name);
}

export async function fetchGateCriteriaTemplates(sbClient, { organisationId, gateType, stageTemplateId }) {
  if (!gateType) return [];

  const { data: orgRows } = await sbClient
    .from('org_gate_criteria_templates')
    .select('id, name, description, group_name, order_index, stage_id, gate_type, template_id')
    .eq('organisation_id', organisationId)
    .eq('gate_type', gateType)
    .eq('is_active', true)
    .order('order_index');

  let templates = orgRows || [];

  if (!templates.length) {
    const { data: platformRows } = await sbClient
      .from('gate_criteria_templates')
      .select('id, name, description, group_name, order_index, stage_id, gate_type')
      .eq('gate_type', gateType)
      .eq('is_default', true)
      .order('order_index');
    templates = platformRows || [];
  }

  if (stageTemplateId) {
    templates = templates.filter((t) => !t.stage_id || t.stage_id === stageTemplateId);
  }

  return templates.filter((t) => isBakeOffCriterion(t.name));
}

export function buildCriteriaSeedRows(templates, gateId, existingCriteria) {
  const existing = new Set((existingCriteria || []).map((c) => (c.name || '').trim().toLowerCase()));
  return (templates || [])
    .filter((t) => t.name && !existing.has(t.name.trim().toLowerCase()))
    .map((t, i) => ({
      gate_id: gateId,
      name: t.name,
      description: t.description || null,
      order_index: t.order_index ?? i + 1,
      criterion_type: 'exit_check',
      rag_forecast: 'red',
      is_met: false,
    }));
}

/** Seed bake-off exit criteria onto a gate; returns inserted rows. */
export async function seedBakeOffCriteria(gate, organisationId, existingCriteria) {
  let stageTemplateId = null;
  if (gate.stage_id) {
    const { data: stage } = await sb
      .from('programme_stages')
      .select('template_id')
      .eq('id', gate.stage_id)
      .maybeSingle();
    stageTemplateId = stage?.template_id || null;
  }

  const templates = await fetchGateCriteriaTemplates(sb, {
    organisationId,
    gateType: gate.gate_type,
    stageTemplateId,
  });

  const bakeOffTemplates = templates.length
    ? templates
    : Object.keys(BAKEOFF_CRITERION_CHECKS).map((name, i) => ({
      name,
      description: null,
      order_index: 10 + i,
    }));

  const rows = buildCriteriaSeedRows(bakeOffTemplates, gate.id, existingCriteria);
  if (!rows.length) return [];

  const { data, error } = await sb.from('gate_criteria').insert(rows).select('*');
  if (error) throw error;
  return data || [];
}

/** Auto-mark bake-off criteria met + green RAG when project readiness satisfies them. */
export async function syncBakeOffCriteriaMet(projectId, programmeId, criteria) {
  const status = await fetchBakeOffGateStatus(projectId, programmeId);
  const updates = [];

  for (const c of criteria || []) {
    const check = BAKEOFF_CRITERION_CHECKS[c.name];
    if (!check) continue;
    const shouldMet = check(status);
    if (shouldMet && (!c.is_met || c.rag_forecast !== 'green')) {
      updates.push({
        id: c.id,
        is_met: true,
        rag_forecast: 'green',
        met_at: c.met_at || new Date().toISOString(),
      });
    }
  }

  for (const patch of updates) {
    const { id, ...fields } = patch;
    await sb.from('gate_criteria').update(fields).eq('id', id);
  }

  return updates.length;
}

/** True when gate looks like the Define / solutioning gate. */
export function isDefineGate(gate, stageName) {
  if (!gate) return false;
  const n = (gate.name || '').toLowerCase();
  if (n.includes('define')) return true;
  return (stageName || '').toLowerCase() === 'define';
}
